import { useState, useEffect, useCallback } from 'react';

export type DensityMode = 'compact' | 'normal' | 'expanded';

export interface AdaptiveViewportState {
  availableHeight: number;
  densityMode: DensityMode;
  isKeyboardOpen: boolean;
}

const THRESHOLDS = {
  compact: 550,  // < 550px = modo compacto (teclado aberto)
  normal: 650,   // 550-650px = viewports muito pequenos
  expanded: 650, // > 650px = maioria dos phones modernos
};

export const useAdaptiveViewport = (): AdaptiveViewportState => {
  const [state, setState] = useState<AdaptiveViewportState>(() => {
    const height = typeof window !== 'undefined' ? window.innerHeight : 800;
    return {
      availableHeight: height,
      densityMode: height < THRESHOLDS.compact ? 'compact' : height < THRESHOLDS.normal ? 'normal' : 'expanded',
      isKeyboardOpen: false,
    };
  });

  const calculateDensityMode = useCallback((height: number): DensityMode => {
    if (height < THRESHOLDS.compact) return 'compact';
    if (height < THRESHOLDS.normal) return 'normal';
    return 'expanded';
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    let previousHeight = viewport?.height ?? window.innerHeight;
    
    const handleResize = () => {
      const height = viewport?.height ?? window.innerHeight;
      
      // Detectar teclado: queda brusca de > 200px
      const isKeyboardOpen = previousHeight - height > 200;
      
      setState({
        availableHeight: height,
        densityMode: calculateDensityMode(height),
        isKeyboardOpen,
      });

      previousHeight = height;
    };

    // Escutar eventos
    if (viewport) {
      viewport.addEventListener('resize', handleResize);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', handleResize);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateDensityMode]);

  return state;
};
